import { Core } from "@faire/design-tokens";
import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import {
  AnimatedUnderlineText,
  UnderlineHoverStyle,
  ZoomHoverStyle,
  ZoomImage,
  ZoomImageContainer,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/HoverStyles";
import { GUIDE_CARDS } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/const";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import {
  OWF_GUIDE_SOURCE_NAME,
  type FaireAcademyGuide,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackOpenWithFaireGuidesLandingPageCtaClick } from "@faire/web-api--source/events/openWithFaire/click/guidesLandingPageCta";
import { trackOpenWithFaireIndividualGuideCtaClick } from "@faire/web-api--source/events/openWithFaire/click/individualGuideCta";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import openWithFaireAcademy from "@faire/web-api--source/routes/www/open-with-faire/academy";
import * as React from "react";
import styled, { css } from "styled-components";

export const OtherGuideLinks: React.FC = () => {
  const [{ guide }] = useGuideStore(["guide"]);
  const guideTitle = guide.title;
  const { pageState } = useGetFaireAcademyPageState();

  return (
    <HoverRow gap="1x" align="center">
      <BorderTypography>
        <StrictLocalMsg
          defaultMessage="Guide: {guideNameFullyTranslatedSentence}"
          description={{
            text: "Text showing the current Open with Faire Academy guide",
            img: "https://cdn.faire.com/fastly/36ef7d397bb636b4fbd030542ca544fbf4a4240d0135cfc1da33ff2b9a45b4d4.png",
          }}
          values={{ guideNameFullyTranslatedSentence: guideTitle }}
        />
      </BorderTypography>
      <Chevron direction="down" />
      <HoverColumn>
        <Typography variant="subheadingSansMedium">
          <StrictLocalMsg
            defaultMessage="Guides"
            description={{
              text: "Header text above links to the Open with Faire Academy guides",
              img: "https://cdn.faire.com/fastly/36ef7d397bb636b4fbd030542ca544fbf4a4240d0135cfc1da33ff2b9a45b4d4.png",
            }}
          />
        </Typography>
        <Spacer height="3x" />
        <Column gap="1x">
          {GUIDE_CARDS.map((guideCard) => (
            <GuideLink
              guide={guideCard}
              isCurrentGuide={guideCard.title === guideTitle}
              key={guideCard.title}
            />
          ))}
        </Column>
        <Spacer height="3x" />
        <FullWidthButton
          variant="secondary"
          linkTo={openWithFaireAcademy()}
          onClick={() =>
            trackOpenWithFaireGuidesLandingPageCtaClick(pageState, guide.id)
          }
        >
          <StrictLocalMsg
            defaultMessage="See all {guidesNumber} guides"
            description={{
              text: "Cta to view all Faire Academy guides. The guidesNumber will always be greater than 1",
              img: "https://cdn.faire.com/fastly/884b8d500c1fb08f0692b5a18b8407e262a754018096c0397bd5897a16864e54.png",
            }}
            values={{ guidesNumber: GUIDE_CARDS.length }}
          />
        </FullWidthButton>
      </HoverColumn>
    </HoverRow>
  );
};

const IMG_HEIGHT = 60;
const IMG_WIDTH = 72;

const GuideLink: React.FC<{
  guide: FaireAcademyGuide;
  isCurrentGuide: boolean;
}> = ({ guide, isCurrentGuide }) => {
  const [{ guide: sourceGuide }] = useGuideStore(["guide"]);
  const isSSR = useIsSSR();
  const { pageState } = useGetFaireAcademyPageState();

  if (isCurrentGuide) {
    return (
      <SpanRow $bgColor={Core.surface.disabled}>
        <img
          loading="lazy"
          src={optimizeImageUrlFillRectangle(
            IImage.build({ url: guide.featuredImage }),
            isSSR,
            IMG_WIDTH * 2,
            IMG_HEIGHT * 2
          )}
          style={{
            height: IMG_HEIGHT,
            width: IMG_WIDTH,
          }}
          alt=""
        />
        <Typography>{guide.title}</Typography>
      </SpanRow>
    );
  }

  return (
    <Link
      to={guide.linkToPage}
      onClick={() =>
        trackOpenWithFaireIndividualGuideCtaClick(
          pageState,
          guide.title,
          OWF_GUIDE_SOURCE_NAME.GUIDES_DROPDOWN,
          sourceGuide.title
        )
      }
    >
      <SpanRow>
        <ZoomImageContainer $height={IMG_HEIGHT} $width={IMG_WIDTH}>
          <ZoomImage
            loading="lazy"
            src={optimizeImageUrlFillRectangle(
              IImage.build({ url: guide.featuredImage }),
              isSSR,
              IMG_WIDTH * 2,
              IMG_HEIGHT * 2
            )}
            height={IMG_HEIGHT}
            width={IMG_WIDTH}
            alt=""
          />
        </ZoomImageContainer>
        <AnimatedUnderlineText>
          <Typography>{guide.title}</Typography>
        </AnimatedUnderlineText>
      </SpanRow>
    </Link>
  );
};

const FullWidthButton = styled(Button)`
  width: 100%;
`;

const BorderTypography = styled(Typography)`
  border-bottom: 1px dashed ${Core.text.default};
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const HoverColumn = styled(Column)`
  padding: ${getSpacing("3x")};
  width: 385px;
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 100%;
  &&& {
    display: none;
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const HoverRow = styled(Row)`
  position: relative;
  padding-bottom: ${getSpacing("2x")};

  &&& {
    &:hover,
    &:active {
      ${HoverColumn} {
        display: block;
      }
    }
  }
`;

const SpanRow = styled.span<{ $bgColor?: string }>`
  display: flex;
  align-items: center;
  gap: ${getSpacing("2x")};

  ${({ $bgColor }) =>
    $bgColor
      ? css`
          background-color: ${$bgColor};
        `
      : null}

  &:hover {
    ${UnderlineHoverStyle}
    ${ZoomHoverStyle}
  }
`;
